import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Download as DownloadIcon, Loader2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { NewsletterOptIn } from "@/components/newsletter/NewsletterOptIn";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface Props {
  downloadId: string;
  downloadSlug: string;
  downloadTitle: string;
  locale: string;
  gateCopy?: string | null;
}

export function LeadMagnetGate({ downloadId, downloadSlug, downloadTitle, locale, gateCopy }: Props) {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [subscribe, setSubscribe] = useState(true);
  const [topics, setTopics] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  function readUtm(): Record<string, string> {
    try {
      const sp = new URLSearchParams(window.location.search);
      const out: Record<string, string> = {};
      ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"].forEach((k) => {
        const v = sp.get(k);
        if (v) out[k] = v.slice(0, 80);
      });
      return out;
    } catch { return {}; }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error(t("newsletter.leadMagnet.invalidEmail", "Please enter a valid email address."));
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("lead-magnet-request", {
        body: {
          download_id: downloadId,
          email: email.trim(),
          name: name.trim() || undefined,
          locale,
          source: `gate:${downloadSlug}`,
          utm: readUtm(),
          subscribe_newsletter: subscribe,
          topics: subscribe ? topics : undefined,
          consent: true,
          website,
        },
      });
      if (error) throw error;
      const status = (data as { status?: string } | null)?.status;
      if (status === "queued_send_failed") {
        toast.error(t("newsletter.leadMagnet.sendIssue", "Saved, but the email is delayed — try again in a minute."));
      }
      setSent(true);
    } catch (err) {
      toast.error((err as Error).message || t("newsletter.leadMagnet.error", "Something went wrong. Please try again."));
    } finally {
      setLoading(false);
    }
  }

  if (sent) {
    return (
      <Card className="border-primary/30 bg-primary/5">
        <CardContent className="space-y-3 p-5 text-center">
          <CheckCircle2 className="mx-auto h-10 w-10 text-primary" />
          <h3 className="font-display text-lg font-semibold">
            {t("newsletter.leadMagnet.successTitle", "Check your inbox")}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t("newsletter.leadMagnet.successBody", "We sent your download link to {{email}}. The link is valid for 72 hours.", { email })}
          </p>
          <p className="text-xs text-muted-foreground">
            {t("newsletter.leadMagnet.successHint", "Don't see it? Check your spam folder, or wait 1–2 minutes.")}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="space-y-4 p-5">
        <div className="flex items-center gap-2">
          <DownloadIcon className="h-5 w-5 text-primary" />
          <h3 className="font-display text-lg font-semibold">
            {t("newsletter.leadMagnet.heading", "Get this free resource")}
          </h3>
        </div>
        <p className="text-sm text-muted-foreground">
          {gateCopy ?? t("newsletter.leadMagnet.body", "Enter your email and we'll send you the download link for {{title}}.", { title: downloadTitle })}
        </p>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="lm-name">{t("newsletter.leadMagnet.nameLabel", "Your name")} <span className="text-xs text-muted-foreground">({t("common.optional", "optional")})</span></Label>
            <Input id="lm-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={100} autoComplete="given-name" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="lm-email">{t("newsletter.leadMagnet.emailLabel", "Email address")}</Label>
            <div className="relative">
              <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input id="lm-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} maxLength={255} autoComplete="email" className="pl-9" />
            </div>
          </div>

          {/* Honeypot */}
          <div aria-hidden className="absolute h-0 w-0 overflow-hidden opacity-0" style={{ position: "absolute", left: "-9999px" }}>
            <label>Website<input tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} /></label>
          </div>

          <NewsletterOptIn
            checked={subscribe}
            onCheckedChange={setSubscribe}
            topics={topics}
            onTopicsChange={setTopics}
            showTopics
            label={t("newsletter.leadMagnet.subscribe", "Also send me the Bootpreneurs weekly digest — practical playbooks, no spam.")}
          />

          <p className="text-xs text-muted-foreground">
            {t("newsletter.leadMagnet.consent", "By submitting, you agree to receive the requested file by email and accept our privacy policy.")}
          </p>

          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <DownloadIcon className="mr-2 h-4 w-4" />}
            {t("newsletter.leadMagnet.cta", "Send me the link")}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}